<template>
    <div ref="rootElement">
        <!-- Header -->
        <div class="mb-5 d-flex flex-column flex-lg-row justify-content-lg-between align-items-lg-center">
            <div v-if="stage === 1">
                <b-row>
                    <b-col
                        cols="12"
                        xl="11"
                    >
                        <div v-if="property">
                            <h1>Your Listing</h1>
                            <p class="text-demibold">
                                Customise and create the perfect property package for you
                            </p>
                            <router-link
                                :to="{ name: 'property', params: { id: property.id } }"
                                tag="button"
                                type="button"
                                class="btn btn--secondary"
                            >
                                Back to Edit Property
                            </router-link>
                        </div>
                        <div v-else>
                            <h1>Your New Listing</h1>
                            <p class="text-demibold">
                                Once you have chosen your listing type, you can choose the package that’s right for you. After you have done this, you are free to choose from our additional package extras so you can customise your property package exactly how you want it to be
                            </p>
                        </div>
                    </b-col>
                </b-row>
            </div>
            <div v-if="stage === 2">
                <h1>Checkout</h1>
                <p class="text-bold">
                    Enter your billing details and purchase your package
                </p>
                <p
                    v-if="!authenticated"
                    class="text-bold"
                >
                    Already registered?
                    <button
                        class="text-primary text-bold px-0"
                        @click="$refs.loginModal.show"
                    >
                        Login
                    </button> to proceed with your purchase.
                </p>
            </div>
            <div class="d-flex align-items-center justify-content-lg-end">
                <!-- Back to basket -->
                <button
                    v-if="stage === 2"
                    type="button"
                    class="btn btn--outline btn--nomin btn--sm mr-5"
                    @click.prevent="prevStage()"
                >
                    Edit Basket
                </button>
                <!-- END Back to basket -->

                <div class="basket-steps mt-4 mt-lg-0">
                    <div
                        :class="{ active : stage === 1 }"
                        class="basket-steps__item"
                    >
                        1
                    </div>
                    <div
                        :class="{ active : stage === 2 }"
                        class="basket-steps__item"
                    >
                        2
                    </div>
                </div>
            </div>
        </div>

        <!-- Basket -->
        <b-row v-if="stage === 1">
            <b-col
                cols="12"
                lg="8"
            >
                <!-- Accordions -->
                <div
                    id="basket-accordion"
                    role="tablist"
                >
                    <!-- Step 1 -->
                    <b-card
                        no-body
                        class="mb-2"
                    >
                        <b-card-header
                            header-tag="header"
                            role="tab"
                        >
                            <button
                                :class="{ disabled: isEditProperty }"
                                :disabled="isEditProperty"
                                type="button"
                                class="h3 text-bold"
                                @click="setStep('type')"
                            >
                                <span class="text-bold color-primary mr-4">1</span>
                                Choose your listing type
                            </button>
                        </b-card-header>
                        <b-collapse
                            id="accordion1"
                            ref="accordionType"
                            v-bind="{ visible : step === 'type' && !isEditProperty }"
                            accordion="my-accordion"
                            role="tabpanel"
                        >
                            <b-card-body>
                                <ul class="list-unstyled mb-0 d-flex flex-column flex-lg-row align-items-center">
                                    <li class="mr-lg-4 mb-4 mb-lg-0">
                                        <!-- Choice 1: Sell -->
                                        <button
                                            @click="addToBasket('type', null, 'sale')"
                                        >
                                            <span class="label-type label-type--sell label-type--xl">Sell</span>
                                        </button>
                                    </li>
                                    <li>
                                        <!-- Choice 2: Let -->
                                        <button
                                            class="basket-choice-1"
                                            @click="addToBasket('type', null, 'let')"
                                        >
                                            <span class="label-type label-type--let label-type--xl">Let</span>
                                        </button>
                                    </li>
                                </ul>
                            </b-card-body>
                        </b-collapse>
                    </b-card>

                    <!-- Step 2 -->
                    <b-card
                        no-body
                        class="mb-2"
                    >
                        <b-card-header
                            header-tag="header"
                            role="tab"
                        >
                            <button
                                :class="{ disabled: isEditProperty || !packageType }"
                                :disabled="isEditProperty || !packageType"
                                type="button"
                                class="h3 text-bold d-flex align-items-center justify-content-between"
                                @click="setStep('packages')"
                            >
                                <div>
                                    <span class="text-bold color-primary mr-4">2</span>
                                    Choose your package
                                </div>
                                <div>
                                    <span
                                        v-if="currentPackage"
                                        :class="{
                                            'color-sell': basket.find(item => item.slug === 'my-basic' || item.slug === 'intro-only'),
                                            'color-let': basket.find(item => item.slug === 'my-classic' || item.slug === 'tenant-find'),
                                            'color-rent': basket.find(item => item.slug === 'my-premium' || item.slug === 'tenant-find-plus'),
                                        }"
                                        class="current-package"
                                    >
                                        {{ currentPackage }}
                                    </span>
                                </div>
                            </button>
                        </b-card-header>
                        <b-collapse
                            id="accordion2"
                            ref="accordionPackages"
                            v-bind="{ visible : !isEditProperty && packageType && step === 'packages' }"
                            accordion="my-accordion"
                            role="tabpanel"
                        >
                            <b-card-body>
                                <!-- Choice 2: Packages -->
                                <div
                                    v-if="packageType === 'sale'"
                                    role="tablist"
                                >
                                    <!-- Package -->
                                    <b-card
                                        v-for="pack in packages.filter(item => item.transaction_type === 'sale')"
                                        :key="pack.id"
                                        :class="['package-' + pack.slug]"
                                        no-body
                                        class="mb-2 package"
                                    >
                                        <b-card-header
                                            header-tag="header"
                                            role="tab"
                                            class="package__header"
                                        >
                                            <!-- Package title -->
                                            <a
                                                v-b-toggle="'accordion-' + pack.id"
                                                block
                                                href="#"
                                                variant="info"
                                                class="package-item text-bold d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between"
                                            >
                                                <span>
                                                    <SVGSprite
                                                        :name="'package-item-' + pack.color"
                                                        class="package-icon-basket"
                                                    />
                                                    <span>{{ pack.name }}</span>
                                                </span>
                                                <span>{{ pack.taxable_price | price(false) }}</span>
                                            </a>
                                        </b-card-header>
                                        <b-collapse
                                            :id="'accordion-' + pack.id"
                                            accordion="my-sub-accordion"
                                            role="tabpanel"
                                        >
                                            <b-card-body>
                                                <p
                                                    v-if="pack.description"
                                                    class="d-block text-demibold mt-0 mb-5"
                                                >
                                                    {{ pack.description }}
                                                </p>

                                                <!-- Package features -->
                                                <ul class="list-unstyled mb-5">
                                                    <li
                                                        v-for="product in products.filter(item => pack.products.indexOf(item.id) !== -1)"
                                                        :key="product.id"
                                                        class="d-flex mb-3"
                                                    >
                                                        <SVGSprite
                                                            :name="product.icon || 'messages'"
                                                            class="feature-icon"
                                                        />
                                                        <p class="mb-0">
                                                            {{ product.name }}
                                                        </p>
                                                    </li>
                                                </ul>

                                                <!-- Add package to basket -->
                                                <div class="d-flex justify-content-start justify-content-lg-end">
                                                    <button
                                                        class="btn btn--secondary"
                                                        @click="addToBasket('package', pack)"
                                                    >
                                                        Choose
                                                    </button>
                                                </div>
                                            </b-card-body>
                                        </b-collapse>
                                    </b-card>
                                </div>
                                <div
                                    v-else-if="packageType === 'let'"
                                    role="tablist"
                                >
                                    <b-card
                                        v-for="pack in packages.filter(item => item.transaction_type === 'let')"
                                        :key="pack.id"
                                        :class="['package-' + pack.slug]"
                                        no-body
                                        class="mb-2 package"
                                    >
                                        <b-card-header
                                            header-tag="header"
                                            role="tab"
                                            class="package__header"
                                        >
                                            <!-- Package title -->
                                            <a
                                                v-b-toggle="'accordion-' + pack.id"
                                                block
                                                href="#"
                                                variant="info"
                                                class="package-item text-bold d-flex flex-column flex-lg-row align-items-lg-center justify-content-lg-between"
                                            >
                                                <span>
                                                    <SVGSprite
                                                        :name="'package-item-' + pack.color"
                                                        class="package-icon-basket"
                                                    />
                                                    <span>{{ pack.name }}</span>
                                                </span>
                                                <span>{{ pack.taxable_price | price(false) }}</span>
                                            </a>
                                        </b-card-header>
                                        <b-collapse
                                            :id="'accordion-' + pack.id"
                                            accordion="my-sub-accordion"
                                            role="tabpanel"
                                        >
                                            <b-card-body>
                                                <p
                                                    v-if="pack.description"
                                                    class="d-block text-demibold mt-0 mb-5"
                                                >
                                                    {{ pack.description }}
                                                </p>

                                                <!-- Package features -->
                                                <ul class="list-unstyled mb-5">
                                                    <li
                                                        v-for="product in products.filter(item => pack.products.indexOf(item.id) !== -1)"
                                                        :key="product.id"
                                                        class="d-flex mb-3"
                                                    >
                                                        <SVGSprite
                                                            :name="product.icon || 'messages'"
                                                            class="feature-icon"
                                                        />
                                                        <p class="mb-0">
                                                            {{ product.name }}
                                                        </p>
                                                    </li>
                                                </ul>

                                                <!-- Add package to basket -->
                                                <div class="d-flex justify-content-start justify-content-lg-end">
                                                    <button
                                                        class="btn btn--secondary"
                                                        @click="addToBasket('package', pack)"
                                                    >
                                                        Choose
                                                    </button>
                                                </div>
                                            </b-card-body>
                                        </b-collapse>
                                    </b-card>
                                </div>
                            </b-card-body>
                        </b-collapse>
                    </b-card>

                    <!-- Step 3 -->
                    <b-card
                        no-body
                        class="mb-2"
                    >
                        <b-card-header
                            header-tag="header"
                            role="tab"
                        >
                            <button
                                :class="{ disabled : !isEditProperty && !selectedPackage }"
                                :disabled="!isEditProperty && !selectedPackage"
                                class="h3 text-bold"
                                @click="setStep('extras')"
                            >
                                <span class="text-bold color-primary mr-4">3</span>
                                My package extras
                            </button>
                        </b-card-header>
                        <b-collapse
                            id="accordion3"
                            ref="accordionExtras"
                            v-bind="{ visible : isEditProperty || (!!selectedPackage && step === 'extras') }"
                            accordion="my-accordion"
                            role="tabpanel"
                        >
                            <b-card-body>
                                <p>Customise your My Online package using our extra options below.</p>

                                <div
                                    v-if="productsHaveLoaded"
                                    class="row mt-5"
                                >
                                    <!-- Extra item -->
                                    <div
                                        v-for="product in products"
                                        :key="product.id"
                                        class="col-12 col-lg-6 col-xl-4 mb-5"
                                    >
                                        <div
                                            :class="{
                                                'product-included': (product.incompatible) || (
                                                    product.singular && (
                                                        product.inSelectedPackage ||
                                                    product.inPurchasedPackage ||
                                                    product.alreadyPurchased ||
                                                    basket.find(item => item.id === product.id && item.type === 'product')
                                                    ))
                                            }"
                                            class="bg-white p-5 text-center h-100 d-flex flex-column align-items-center"
                                        >
                                            <SVGSprite
                                                :name="product.icon || 'messages'"
                                                class="extras-icon"
                                            />
                                            <h3 class="mb-3">
                                                {{ product.name }}
                                            </h3>
                                            <h4 class="text-bold color-mid-grey mb-3">
                                                {{ product.taxable_price | price(false) }}
                                            </h4>
                                            <div class="mt-auto">
                                                <div class="mt-5">
                                                    <button
                                                        v-if="product.description"
                                                        type="button"
                                                        class="d-flex align-items-center color-primary mx-auto mb-5"
                                                        @click="showProductInfo(product)"
                                                    >
                                                        <SVGSprite
                                                            name="info"
                                                            class="info-icon mr-2"
                                                        />
                                                        <span>More info</span>
                                                    </button>
                                                    <p v-if="product.incompatible">
                                                        Not available
                                                    </p>
                                                    <p v-else-if="product.singular && basket.find(item => item.id === product.id && item.type === 'product')">
                                                        Added to basket
                                                    </p>
                                                    <p v-else-if="product.singular && product.alreadyPurchased">
                                                        Product already purchased
                                                    </p>
                                                    <p v-else-if="product.singular && (product.inSelectedPackage || product.inPurchasedPackage)">
                                                        Included in package
                                                    </p>
                                                    <button
                                                        v-else
                                                        type="button"
                                                        class="btn btn--secondary btn--nomin w-100"
                                                        @click="addToBasket('product', product)"
                                                    >
                                                        Add
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <b-modal
                                        v-if="modalProduct"
                                        ref="modalProductDescription"
                                        :title="modalProduct.name"
                                        centered
                                        hide-footer
                                    >
                                        <div
                                            v-if="modalProduct.description"
                                            v-html="modalProduct.description"
                                        />
                                    </b-modal>
                                </div>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                </div>
            </b-col>
            <b-col
                cols="12"
                lg="4"
                class="sidebar-sticky"
            >
                <div class="bg-white p-5">
                    <!-- Basket items -->
                    <div class="table-flex table-flex--plain table-flex--noborder table-flex--cell-2-xs table-flex--cell-3-sm table-flex--cell-4-xs basket-items">
                        <div class="table-flex--header">
                            <div><span class="color-mid-grey">Product</span></div>
                            <div />
                            <div />
                            <div />
                        </div>
                        <div
                            v-for="item in basket"
                            :key="`${item.type}-${item.id}`"
                            :class="'basket-item--' + item.type"
                            class="basket-item"
                        >
                            <div>{{ item.name }}</div>
                            <div>{{ item.quantity }}</div>
                            <div>{{ item.taxable_price | price }}</div>
                            <div>
                                <button
                                    v-if="item.type == 'package'"
                                    class="remove"
                                    @click="removeFromBasket(item.type, item)"
                                />
                                <button
                                    v-else
                                    class="remove"
                                    @click="removeFromBasket(item.type, item)"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Basket totals -->
                    <div class="d-flex align-items-center justify-content-between px-3 basket-totals">
                        <div><span class="color-dark-grey">Subtotal (Excluding VAT)</span></div>
                        <div><span class="color-dark-grey">{{ subtotal.net | price }}</span></div>
                    </div>

                    <div class="d-flex align-items-center justify-content-between px-3 mt-2">
                        <div><span class="color-dark-grey">VAT</span></div>
                        <div><span class="color-dark-grey">{{ tax.net | price }}</span></div>
                    </div>

                    <div class="d-flex align-items-center justify-content-between px-3 mt-2">
                        <div><span class="text-bold color-dark-grey">Total</span></div>
                        <div><span class="text-bold color-dark-grey">{{ total.net | price }}</span></div>
                    </div>

                    <div class="mt-5">
                        <button
                            :class="{ disabled : basket.length === 0 }"
                            type="button"
                            class="btn btn--primary w-100"
                            @click.prevent="nextStage()"
                        >
                            Checkout
                        </button>
                    </div>
                </div>
            </b-col>
        </b-row>
        <!-- END Basket -->

        <!-- Checkout -->
        <b-row v-if="stage === 2">
            <b-col
                cols="12"
                lg="8"
            >
                <!-- Field(s): Name -->
                <b-row>
                    <b-col
                        cols="12"
                        lg="6"
                    >
                        <!-- Field: Forename -->
                        <div class="field-wrap">
                            <h4 class="mb-3 color-mid-grey text-bold">
                                Forename
                            </h4>
                            <label
                                for="field-forename"
                                class="sr-only"
                            >
                                Forename
                            </label>
                            <div class="field-wrap__input">
                                <input
                                    id="field-forename"
                                    v-model="details.first_name"
                                    v-validate="{ required: true }"
                                    :class="{ 'has-error': showErrors && errors.has('field-forename') }"
                                    type="text"
                                    name="field-forename"
                                    placeholder="Forename"
                                >
                            </div>
                            <div
                                v-if="showErrors && errors.has('field-forename')"
                                class="py-3"
                            >
                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                    Please enter your forename
                                </div>
                            </div>
                        </div>
                        <!-- END Field: Forename -->
                    </b-col>
                    <b-col
                        cols="12"
                        lg="6"
                    >
                        <!-- Field: Surname -->
                        <div class="field-wrap">
                            <h4 class="mb-3 color-mid-grey text-bold">
                                Surname
                            </h4>
                            <label
                                for="field-surname"
                                class="sr-only"
                            >
                                Surname
                            </label>
                            <div class="field-wrap__input">
                                <input
                                    id="field-surname"
                                    v-model="details.last_name"
                                    v-validate="{ required: true }"
                                    :class="{ 'has-error': showErrors && errors.has('field-surname') }"
                                    type="text"
                                    name="field-surname"
                                    placeholder="Surname"
                                >
                            </div>
                            <div
                                v-if="showErrors && errors.has('field-surname')"
                                class="py-3"
                            >
                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                    Please enter your surname
                                </div>
                            </div>
                        </div>
                        <!-- END Field: Surname -->
                    </b-col>
                </b-row>
                <!-- END Field(s): Name -->

                <!-- Field(s): Contact -->
                <b-row>
                    <b-col
                        cols="12"
                    >
                        <!-- Field: Email -->
                        <div class="field-wrap">
                            <h4 class="mb-3 color-mid-grey text-bold">
                                Email Address
                            </h4>
                            <label
                                for="field-email"
                                class="sr-only"
                            >
                                Email Address
                            </label>
                            <div class="field-wrap__input">
                                <input
                                    id="field-email"
                                    v-model="details.email"
                                    v-validate="{
                                        required: true,
                                        email: true
                                    }"
                                    :class="{ 'has-error': showErrors && errors.has('field-email') }"
                                    type="email"
                                    name="field-email"
                                    placeholder="Email Address"
                                >
                            </div>
                            <div
                                v-if="showErrors && errors.has('field-email')"
                                class="py-3"
                            >
                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                    Please enter a valid email address
                                </div>
                            </div>
                        </div>
                        <!-- END Field: Email -->
                    </b-col>
                </b-row>
                <b-row>
                    <b-col
                        cols="12"
                        lg="6"
                    >
                        <!-- Field: Mobile telephone -->
                        <div class="field-wrap">
                            <h4 class="mb-3 color-mid-grey text-bold">
                                Telephone
                            </h4>
                            <label
                                for="field-tel"
                                class="sr-only"
                            >
                                Telephone
                            </label>
                            <div class="field-wrap__input">
                                <input
                                    id="field-tel"
                                    v-model="details.telephone"
                                    v-validate="{
                                        required: true,
                                        regex: /^\+?[0-9 ]+$/
                                    }"
                                    :class="{ 'has-error': showErrors && errors.has('field-tel') }"
                                    type="tel"
                                    name="field-tel"
                                    placeholder="Telephone"
                                >
                            </div>
                            <div
                                v-if="showErrors && errors.has('field-tel')"
                                class="py-3"
                            >
                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                    Please enter a valid telephone number
                                </div>
                            </div>
                        </div>
                        <!-- END Field: Mobile telephone -->
                    </b-col>
                    <b-col
                        cols="12"
                        lg="6"
                    >
                        <!-- Field: Mobile telephone -->
                        <div class="field-wrap">
                            <h4 class="mb-3 color-mid-grey text-bold">
                                Mobile Telephone
                            </h4>
                            <label
                                for="field-tel"
                                class="sr-only"
                            >
                                Mobile Telephone
                            </label>
                            <div class="field-wrap__input">
                                <input
                                    id="field-mobile-tel"
                                    v-model="details.mobile_telephone"
                                    v-validate="{
                                        required: true,
                                        regex: /^\+?[0-9 ]+$/
                                    }"
                                    :class="{ 'has-error': showErrors && errors.has('field-mobile-tel') }"
                                    type="tel"
                                    name="field-mobile-tel"
                                    placeholder="Mobile Telephone"
                                >
                            </div>
                            <div
                                v-if="showErrors && errors.has('field-mobile-tel')"
                                class="py-3"
                            >
                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                    Please enter a valid mobile telephone number
                                </div>
                            </div>
                        </div>
                        <!-- END Field: Mobile telephone -->
                    </b-col>
                </b-row>
                <!-- END Field(s): Contact -->

                <b-row v-if="!authenticated">
                    <b-col
                        cols="12"
                        lg="6"
                    >
                        <!-- Password -->
                        <div class="field-wrap">
                            <label
                                for="password"
                                class="sr-only"
                            >
                                Password
                            </label>
                            <h4 class="mb-3 color-mid-grey text-bold">
                                Password
                            </h4>
                            <div class="field-wrap__input">
                                <password
                                    id="password"
                                    ref="password"
                                    v-model="details.password"
                                    v-validate="{ required: true }"
                                    :toggle="true"
                                    :badge="false"
                                />
                            </div>
                            <div
                                v-if="showErrors && errors.has('password')"
                                class="py-3"
                            >
                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                    Please enter a valid password
                                </div>
                            </div>
                        </div>
                        <!-- END Password -->
                    </b-col>
                    <b-col
                        cols="12"
                        lg="6"
                    >
                        <!-- Confirm Password -->
                        <div class="field-wrap">
                            <label
                                for="field-password-confirm"
                                class="sr-only"
                            >
                                Confirm Password
                            </label>
                            <h4 class="mb-3 color-mid-grey text-bold">
                                Confirm Password
                            </h4>
                            <div class="field-wrap__input">
                                <input
                                    id="field-password-confirm"
                                    v-model="details.password_confirmation"
                                    v-validate="{ required: true, confirmed: 'password' }"
                                    type="password"
                                    name="passwordConfirmation"
                                    placeholder="Please confirm your password"
                                >
                            </div>
                            <div
                                v-if="showErrors && errors.has('field-password-confirm')"
                                class="py-3"
                            >
                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                    Your password needs to match
                                </div>
                            </div>
                        </div>
                        <!-- END Confirm Password -->
                    </b-col>
                </b-row>

                <!-- Sub heading -->
                <h4 class="mb-3 color-mid-grey text-bold">
                    Address
                </h4>

                <b-row
                    v-if="addresses.length"
                >
                    <b-col
                        cols="12"
                    >
                        <div class="field-wrap__input field-wrap__input--select mb-4">
                            <select
                                id="field-selectaddress"
                                v-model="details.address_id"
                                v-validate="{ required: true }"
                                name="field-selectaddress"
                            >
                                <option
                                    :value="null"
                                    disabled
                                    selected
                                    hidden
                                >
                                    Select an address
                                </option>
                                <option
                                    v-for="address in addresses"
                                    :key="address.id"
                                    :value="address.id"
                                >
                                    {{ address.label }}
                                </option>
                                <option value="new">
                                    Add a new address
                                </option>
                            </select>
                        </div>
                    </b-col>
                </b-row>

                <div v-if="details.address_id === 'new'">
                    <!-- Field(s): Address -->
                    <b-row>
                        <b-col
                            cols="12"
                            lg="6"
                        >
                            <!-- Field: Search address -->
                            <div class="field-wrap">
                                <h4 class="mb-3 color-mid-grey text-bold">
                                    Address Lookup
                                </h4>
                                <label
                                    for="field-search"
                                    class="sr-only"
                                >
                                    Postcode
                                </label>
                                <div class="field-wrap__input field-wrap__input--prepend field-wrap__input--prepend-fill">
                                    <input
                                        id="field-search"
                                        v-model="postcodeSearch"
                                        type="search"
                                        name="field-search"
                                        placeholder="Postcode"
                                    >
                                    <div class="field-wrap__input-icon">
                                        <SVGSprite
                                            name="search"
                                            class="form-icon"
                                        />
                                    </div>
                                </div>
                            </div>
                            <button
                                class="btn btn--secondary mb-5"
                                @click.prevent="searchAddress()"
                            >
                                Search Address
                            </button>
                            <!-- END Field: Search address -->
                        </b-col>
                        <b-col
                            cols="12"
                            lg="6"
                        >
                            <!-- Field: Address Line 1 -->
                            <div class="field-wrap">
                                <h4 class="mb-3 color-mid-grey text-bold">
                                    Address
                                </h4>
                                <label
                                    for="field-address-1"
                                    class="sr-only"
                                >
                                    Address Line 1
                                </label>
                                <div class="field-wrap__input mb-4">
                                    <input
                                        id="field-address-1"
                                        v-model="details.address"
                                        v-validate="{ required: true }"
                                        :class="{ 'has-error': showErrors && errors.has('field-address-1') }"
                                        type="text"
                                        name="field-address-1"
                                        placeholder="Address Line 1"
                                    >
                                </div>
                                <div
                                    v-if="showErrors && errors.has('field-address-1')"
                                    class="py-3"
                                >
                                    <div class="d-inline-block alert-error p-3 rounded-corners">
                                        Please enter your address
                                    </div>
                                </div>
                            </div>
                            <!-- END Field: Address Line 1 -->
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col
                            cols="12"
                            lg="6"
                        >
                            <!-- Field: Address Line 2 -->
                            <div class="field-wrap">
                                <h4 class="mb-3 color-mid-grey text-bold">
                                    Address (continued)
                                </h4>
                                <label
                                    for="field-address-1"
                                    class="sr-only"
                                >
                                    Address Line 2
                                </label>
                                <div class="field-wrap__input mb-4">
                                    <input
                                        id="field-address-2"
                                        v-model="details.address2"
                                        :class="{ 'has-error': showErrors && errors.has('field-address-2') }"
                                        type="text"
                                        name="field-address-2"
                                        placeholder="Address Line 2"
                                    >
                                </div>
                            </div>
                            <!-- END Field: Address Line 1 -->
                        </b-col>
                        <b-col
                            cols="12"
                            lg="6"
                        >
                            <!-- Field: Town / City -->
                            <div class="field-wrap">
                                <h4 class="mb-3 color-mid-grey text-bold">
                                    Town / City
                                </h4>
                                <label
                                    for="field-town-city"
                                    class="sr-only"
                                >
                                    Town / City
                                </label>
                                <div class="field-wrap__input mb-4">
                                    <input
                                        id="field-town-city"
                                        v-model="details.town"
                                        v-validate="{ required: true }"
                                        :class="{ 'has-error': showErrors && errors.has('field-town-city') }"
                                        type="text"
                                        name="field-town-city"
                                        placeholder="Town / City"
                                    >
                                </div>
                                <div
                                    v-if="showErrors && errors.has('field-town-city')"
                                    class="py-3"
                                >
                                    <div class="d-inline-block alert-error p-3 rounded-corners">
                                        Please enter your town or city
                                    </div>
                                </div>
                            </div>
                            <!-- END Field: Town / City -->
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col
                            cols="12"
                            lg="6"
                        >
                            <!-- Field: County -->
                            <div class="field-wrap">
                                <h4 class="mb-3 color-mid-grey text-bold">
                                    County
                                </h4>
                                <label
                                    for="field-county"
                                    class="sr-only"
                                >
                                    County
                                </label>
                                <div class="field-wrap__input mb-4">
                                    <input
                                        id="field-county"
                                        v-model="details.county"
                                        type="text"
                                        name="field-county"
                                        placeholder="County"
                                    >
                                </div>
                            </div>
                            <!-- END Field: County -->
                        </b-col>
                        <b-col
                            cols="12"
                            lg="6"
                        >
                            <!-- Field: Postcode -->
                            <div class="field-wrap">
                                <h4 class="mb-3 color-mid-grey text-bold">
                                    Postcode
                                </h4>
                                <label
                                    for="field-postcode"
                                    class="sr-only"
                                >
                                    Postcode
                                </label>
                                <div class="field-wrap__input mb-4">
                                    <input
                                        id="field-postcode"
                                        v-model="details.postcode"
                                        v-validate="{ required: true }"
                                        :class="{ 'has-error': showErrors && errors.has('field-postcode') }"
                                        type="text"
                                        name="field-postcode"
                                        placeholder="Postcode"
                                    >
                                </div>
                                <div
                                    v-if="showErrors && errors.has('field-postcode')"
                                    class="py-3"
                                >
                                    <div class="d-inline-block alert-error p-3 rounded-corners">
                                        Please enter your postcode
                                    </div>
                                </div>
                            </div>
                            <!-- END Field: Postcode -->
                        </b-col>
                    </b-row>

                    <b-row>
                        <b-col
                            cols="12"
                            lg="6"
                        >
                            <!-- Field: Country -->
                            <div class="field-wrap">
                                <h4 class="mb-3 color-mid-grey text-bold">
                                    Country
                                </h4>
                                <label
                                    for="field-country"
                                    class="sr-only"
                                >
                                    Country
                                </label>
                                <div class="field-wrap__input mb-4">
                                    <input
                                        id="field-country"
                                        v-model="details.country"
                                        v-validate="{ required: true }"
                                        :class="{ 'has-error': showErrors && errors.has('field-country') }"
                                        type="text"
                                        name="field-country"
                                        placeholder="Country"
                                    >
                                </div>
                                <div
                                    v-if="showErrors && errors.has('field-country')"
                                    class="py-3"
                                >
                                    <div class="d-inline-block alert-error p-3 rounded-corners">
                                        Please select your country
                                    </div>
                                </div>
                            </div>
                            <!-- END Field: Country -->
                        </b-col>
                    </b-row>
                </div>

                <!-- END Field(s): Address -->
                <b-row v-if="!authenticated">
                    <b-col
                        cols="12"
                    >
                        <!-- Field: Essentials -->
                        <div class="field-wrap">
                            <div class="field-wrap__input field-wrap__input--checkbox mb-3">
                                <input
                                    id="field-essentials-1"
                                    v-model="details.property_to_sell"
                                    :true-value="true"
                                    :false-value="false"
                                    type="checkbox"
                                    name="field-essentials"
                                >
                                <label for="field-essentials-1"><span class="check-element" />I have a property to sell</label>
                            </div>
                            <div class="field-wrap__input field-wrap__input--checkbox mb-3">
                                <input
                                    id="field-essentials-2"
                                    v-model="details.has_mortgage"
                                    :true-value="false"
                                    :false-value="true"
                                    type="checkbox"
                                    name="field-essentials"
                                >
                                <label for="field-essentials-2"><span class="check-element" />I require a mortgage</label>
                            </div>
                            <div class="field-wrap__input field-wrap__input--checkbox mb-3">
                                <input
                                    id="field-essentials-3"
                                    v-model="details.looking_to_buy"
                                    :true-value="true"
                                    :false-value="false"
                                    type="checkbox"
                                    name="field-essentials"
                                >
                                <label for="field-essentials-3"><span class="check-element" />I'm looking to buy a new house</label>
                            </div>
                        </div>
                        <!-- END Field: Essentials -->
                    </b-col>
                </b-row>
            </b-col>
            <b-col
                cols="12"
                lg="4"
                class="sidebar-sticky"
            >
                <!-- Checkout summary -->
                <div class="bg-white p-5">
                    <!-- Basket items -->
                    <div class="table-flex table-flex--plain table-flex--noborder table-flex--cell-2-xs table-flex--cell-3-xs basket-items">
                        <div class="table-flex--header">
                            <div><span class="color-mid-grey">Product</span></div>
                            <div />
                            <div />
                        </div>
                        <div
                            v-for="item in basket"
                            :key="`${item.type}-${item.id}`"
                            :class="'basket-item--' + item.type"
                            class="basket-item"
                        >
                            <div>{{ item.name }}</div>
                            <div>{{ item.quantity }}</div>
                            <div>{{ item.taxable_price | price }}</div>
                        </div>
                    </div>

                    <!-- Basket totals -->
                    <div class="d-flex align-items-center justify-content-between px-3 basket-totals">
                        <div><span class="color-dark-grey">Subtotal (Excluding VAT)</span></div>
                        <div><span class="color-dark-grey">{{ subtotal.net | price }}</span></div>
                    </div>

                    <div class="d-flex align-items-center justify-content-between px-3 mt-2">
                        <div><span class="color-dark-grey">VAT</span></div>
                        <div><span class="color-dark-grey">{{ tax.net | price }}</span></div>
                    </div>

                    <div class="d-flex align-items-center justify-content-between px-3 mt-2 mb-5">
                        <div><span class="text-bold color-dark-grey">Total</span></div>
                        <div><span class="text-bold color-dark-grey">{{ total.net | price }}</span></div>
                    </div>

                    <h3 class="mb-5 px-3 text-bold basket-totals">
                        Payment Details
                    </h3>

                    <div class="d-flex align-items-center justify-content-between px-3 mt-2">
                        <div class="field-wrap mb-0">
                            <h4 class="mb-4 color-mid-grey text-bold">
                                Card Number
                            </h4>
                            <label
                                for="field-card-number"
                                class="sr-only"
                            >
                                Card Number
                            </label>
                            <div class="field-wrap__input field-wrap__input--border-light mb-4">
                                <input
                                    id="field-card-number"
                                    v-model="details.card.number"
                                    v-validate="{ required: true, credit_card: true }"
                                    :class="{ 'has-error': showErrors && errors.has('field-card-number') }"
                                    type="text"
                                    name="field-card-number"
                                    placeholder="Card Number"
                                >
                            </div>
                            <div
                                v-if="showErrors && errors.has('field-card-number')"
                                class="py-3"
                            >
                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                    Please enter a valid credit/debit card number
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="d-flex align-items-center justify-content-between px-3 mt-2">
                        <div class="field-wrap mb-0">
                            <div class="field-wrap__input field-wrap__input--border-light mb-4">
                                <h4 class="mb-4 color-mid-grey text-bold">
                                    Card Owner Name
                                </h4>
                                <label
                                    for="field-card-number"
                                    class="sr-only"
                                >
                                    Card Name
                                </label>
                                <input
                                    id="field-card-name"
                                    v-model="details.card.name"
                                    v-validate="{ required: true }"
                                    :class="{ 'has-error': showErrors && errors.has('field-card-name') }"
                                    type="text"
                                    name="field-card-name"
                                    placeholder="Card Name"
                                >
                            </div>
                            <div
                                v-if="showErrors && errors.has('field-card-name')"
                                class="py-3"
                            >
                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                    Please enter the card owner's name
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="d-flex align-items-center justify-content-between px-3 mt-2 expiry-cv2">
                        <div class="d-flex align-items-center justify-content-between flex-wrap expiry-fields">
                            <h4 class="mb-4 color-mid-grey text-bold w-100">
                                Expiry Date
                            </h4>
                            <label
                                for="field-card-month"
                                class="sr-only"
                            >
                                Expiry Date
                            </label>
                            <div class="field-wrap expiry-field mb-0">
                                <div class="field-wrap__input field-wrap__input--border-light mb-4">
                                    <input
                                        id="field-card-month"
                                        v-model="details.card.expiry_month"
                                        v-validate="{ required: true }"
                                        :class="{ 'has-error': showErrors && errors.has('field-card-month') }"
                                        type="text"
                                        name="field-card-month"
                                        placeholder="MM"
                                    >
                                </div>
                            </div>
                            <div class="field-wrap expiry-field mb-0">
                                <div class="field-wrap__input field-wrap__input--border-light expiry-field mb-4">
                                    <input
                                        id="field-card-year"
                                        v-model="details.card.expiry_year"
                                        v-validate="{ required: true }"
                                        :class="{ 'has-error': showErrors && errors.has('field-card-year') }"
                                        type="text"
                                        name="field-card-year"
                                        placeholder="YYYY"
                                    >
                                </div>
                            </div>
                            <div
                                v-if="showErrors && (errors.has('field-card-month') || errors.has('field-card-month'))"
                                class="w-100 py-3"
                            >
                                <div class="d-inline-block alert-error p-3 rounded-corners">
                                    Please enter the card expiry date
                                </div>
                            </div>
                        </div>
                        <div class="cv2-field">
                            <h4 class="mb-4 color-mid-grey text-bold w-100 d-flex align-items-center justify-content-between">
                                <span
                                    v-b-tooltip.hover
                                    title="Required field"
                                >
                                    CV2
                                    <span class="required">*</span>
                                </span>
                                <SVGSprite
                                    v-b-tooltip.hover
                                    name="info-question"
                                    class="icon-tooltip"
                                    title="This is the 3 digit number on the back of your card"
                                />
                            </h4>

                            <div class="field-wrap mb-0">
                                <div class="field-wrap__input field-wrap__input--border-light mb-4">
                                    <input
                                        id="field-card-cv2"
                                        v-model="details.card.cv2"
                                        v-validate="{ required: true }"
                                        :class="{ 'has-error': showErrors && errors.has('field-card-cv2') }"
                                        type="text"
                                        name="field-card-cv2"
                                        placeholder="CV2"
                                    >
                                </div>
                                <div
                                    v-if="showErrors && (errors.has('field-card-month') || errors.has('field-card-month'))"
                                    class="w-100 py-3"
                                >
                                    <div class="d-inline-block alert-error p-3 rounded-corners">
                                        Please enter the card CV2 value
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="field-wrap mt-5 p-sm">
                        <div class="field-wrap__input field-wrap__input--checkbox field-wrap__input--acceptance">
                            <input
                                id="field-agree"
                                v-model="billing.agree"
                                :true-value="true"
                                :false-value="false"
                                type="checkbox"
                                name="field-agree"
                            >
                            <label for="field-agree"><span class="check-element check-element--invert" /><span>By submitting the following form you agree for your data to be used in accordance with the terms set out in our <router-link
                                :to="{ name: 'privacy-policy' }"
                                target="_blank"
                            >Privacy Policy</router-link> and <router-link
                                :to="{ name: 'terms-conditions' }"
                                target="_blank"
                            >Terms &amp; Conditions</router-link></span></label>
                        </div>
                    </div>

                    <!-- Purchase order -->
                    <div class="mt-5">
                        <MButton
                            :class="{ disabled : !billing.agree || processing }"
                            :disabled="!billing.agree || processing"
                            :processing="processing"
                            type="button"
                            class="btn btn--primary w-100"
                            spinner-variant="white"
                            @click.prevent="processOrder()"
                        >
                            Buy now
                        </MButton>
                    </div>
                    <!-- END Purchase order -->
                </div>
                <!-- END Checkout summary -->
            </b-col>
        </b-row>
        <!-- END Checkout -->
        <b-modal
            ref="loginModal"
            title="Login"
            class="login-modal"
            hide-footer
        >
            <div class="login-form login-form-checkout">
                <div>
                    <div
                        v-if="loginError"
                        class="alert-danger p-2 mb-4"
                        role="alert"
                    >
                        {{ loginError }}
                    </div>
                    <form @submit.prevent="submitLogin">
                        <div class="field-wrap">
                            <label
                                for="email"
                            >
                                Email
                            </label>
                            <div class="field-wrap__input">
                                <input
                                    id="email"
                                    v-model="loginCredentials.email"
                                    type="email"
                                    name="email"
                                    placeholder=""
                                >
                            </div>
                        </div>

                        <div class="field-wrap">
                            <label
                                for="login-password"
                            >
                                Password
                            </label>
                            <div class="field-wrap__input">
                                <input
                                    id="login-password"
                                    v-model="loginCredentials.password"
                                    type="password"
                                    name="password"
                                    placeholder=""
                                >
                            </div>
                        </div>

                        <MButton
                            :disabled="loginProcessing"
                            :processing="loginProcessing"
                            type="submit"
                            class="btn btn--secondary btn-block w-100"
                        >
                            {{ loginProcessing ? 'Logging in...' : 'Login' }}
                        </MButton>
                    </form>
                </div>
            </div>
        </b-modal>
        <!-- Property lookup modal -->
        <PostcodeLookup
            ref="postcodeLookup"
            @propertySelected="prefillAddress"
            @pageLoaderRemove="hidePageLoader"
        />
        <!-- END Property lookup modal -->

        <transition name="fade">
            <div
                v-if="showSticky"
                class="total-sticky"
            >
                {{ total.net | price }}
            </div>
        </transition>
    </div>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { pickBy } from 'lodash-es';
import Password from 'vue-password-strength-meter';
import PostcodeLookup from '../components/PostcodeLookup';

export default {
    name: 'Basket',
    components: {
        Password,
        PostcodeLookup
    },
    data() {
        return {
            stage: 1,
            step: 'type',
            steps: {
                type: true,
                packages: false,
                extras: false
            },
            loginCredentials: {
                email: '',
                password: ''
            },
            details: {
                first_name: '',
                last_name: '',
                email: '',
                telephone: '',
                mobile_telephone: '',
                password: '',
                password_confirmation: '',
                address_id: null,
                address: '',
                address2: '',
                town: '',
                county: '',
                postcode: '',
                country: '',
                property_to_sell: false,
                has_mortgage: true,
                looking_to_buy: false,
                card: {
                    number: '',
                    name: '',
                    expiry_month: '',
                    expiry_year: '',
                    cv2: ''
                }
            },
            billing: {
                agree: false
            },
            packageType: '',
            showErrors: false,
            processing: false,
            loginError: '',
            loginProcessing: false,
            postcodeSearch: '',
            pageLoader: false,
            modalProduct: null,
            scrolled: 0,
            windowSize: 0,
            showSticky: false
        };
    },
    computed: {
        ...mapState('global', ['packages']),
        ...mapState('properties', ['property']),
        ...mapState('user', ['user']),
        ...mapGetters('user', ['addresses', 'authenticated']),
        ...mapGetters('checkout', ['products', 'productsHaveLoaded', 'basket', 'selectedPackage', 'subtotal', 'tax', 'total']),
        currentPackage() {
            try {
                return this.selectedPackage.name;
            } catch (e) {
                return '';
            }
        },
        isEditProperty() {
            return Boolean(this.$route.params.propertyId);
        }
    },
    created() {
        if (this.$route.params.propertyId) {
            this.getProperty({ propertyId: this.$route.params.propertyId })
                .then(() => {
                    this.steps.type = false;
                    this.steps.extras = true;
                });
        }
        else {
            this.clearProperty();
            if (this.packages.length) {
                this.populatePackageFromQuery();
            }
            else {
                this.getGlobalData().then(() => {
                    this.populatePackageFromQuery();
                });
            }
        }

        this.checkAuthentication({
            requiresAuth: true,
            enforceRedirect: false
        })
            .then((result) => {
                this.prepopulateForm();

                if (result && this.authenticated && this.user && this.user.primary_address_id) {
                    this.details.address_id = this.user.primary_address.id;
                }
                else if (!this.user || !this.user.addresses || !this.user.addresses.length) {
                    this.details.address_id = 'new';
                }
            });
    },
    mounted() {
        // show / hide sticky total
        window.addEventListener('scroll', this.handleScroll);
        window.addEventListener('load', this.getWindowSize);
        window.addEventListener('resize', this.getWindowSize);
    },
    beforeDestroy() {
        this.removePackage();
    },
    destroyed() {
        // remove event listeners for scroll
        window.removeEventListener('scroll', this.handleScroll);
        window.removeEventListener('load', this.getWindowSize);
        window.removeEventListener('resize', this.getWindowSize);
    },
    methods: {
        ...mapActions('global', {
            getGlobalData: 'get'
        }),
        ...mapActions('user', ['checkAuthentication', 'login']),
        ...mapActions('checkout', ['setPackage', 'removePackage', 'addProduct', 'removeProduct', 'submitOrder']),
        ...mapActions('properties', ['getProperty', 'clearProperty']),
        populatePackageFromQuery() {
            if (this.$route.query.package) {
                const selected = this.packages.find(item => item.id === parseInt(this.$route.query.package, 10));
                if (selected) {
                    this.packageType = selected.transaction_type;
                    this.addToBasket('package', selected);
                }
                this.setStep('packages');
            }
        },
        // Enables / Disables the steps and adds item to basket
        addToBasket(step, product, label) {
            // Enable the next step and disable current step (except for type)
            if (step === 'type') {
                if (this.packageType !== label) {
                    this.packageType = label;
                    this.removePackage();
                }
                this.setStep('packages');
            }
            else if (step === 'package') {
                this.setPackage(product);
                this.setStep('extras');
            }
            else if (step === 'product') {
                this.addProduct(product);
            }
        },
        // Removes item from basket and returns user to correct step
        removeFromBasket(type, product) {
            // Return user to correct step
            if (type === 'package') {
                this.removePackage();
            }
            else if (type === 'product') {
                this.removeProduct(product);
            }
        },
        // Enables a basket accordion step
        enableStep(step) {
            // Enable step
            this.steps[step] = true;
        },
        // Disables a basket accordion step
        disableStep(step) {
            // Disable step
            this.steps[step] = false;
        },
        // Goes to checkout page
        nextStage() {
            this.stage++;
        },
        // Goes to basket page
        prevStage() {
            this.stage--;
        },
        submitLogin() {
            this.loginProcessing = true;
            this.login({
                ...this.loginCredentials,
                redirect: false
            }).then(() => {
                this.$refs.loginModal.hide();
                this.loginProcessing = false;
                this.prepopulateForm();
                this.$toasted.success('You have successfully logged in');
            });
        },
        // Purchase order
        processOrder() {
            this.$validator.validateAll().then((result) => {
                if (result) {
                    this.processing = true;
                    this.submitOrder({
                        type: this.packageType,
                        ...pickBy(this.details, (value, key) => {
                            if (this.details.address_id !== 'new' && ['address', 'address2', 'town', 'county', 'postcode', 'country'].indexOf(key) !== -1) {
                                return false;
                            }
                            return value !== null;
                        })
                    })
                        .then(({ data }) => {
                            if (data.payload && data.payload.action === 'redirect') {
                                this.$toasted.info('Redirecting for payment authentication');

                                const form = document.createElement('form');
                                this.$refs.rootElement.appendChild(form);

                                form.method = 'post';
                                form.action = data.payload.target.url;
                                Object.keys(data.payload.target.data).forEach((param) => {
                                    const input = document.createElement('input');
                                    input.type = 'hidden';
                                    input.name = param;
                                    input.value = data.payload.target.data[param];
                                    form.appendChild(input);
                                });
                                form.submit();
                            }
                            else {
                                this.$toasted.success('Thank you, your order has been successfully processed!');
                                this.removePackage();
                                this.processing = false;
                                this.$router.push({ name: 'my-properties' });
                            }
                        })
                        .catch(({ response }) => {
                            this.$toasted.error(response.data.message);
                            this.processing = false;
                        });
                }
                else {
                    this.showErrors = true;
                }
            });
        },
        showPageLoader() {
            this.pageLoader = true;
        },
        hidePageLoader() {
            this.pageLoader = false;
        },
        searchAddress() {
            if (this.postcodeSearch) {
                this.showPageLoader();
                this.$refs.postcodeLookup.findAddress(this.postcodeSearch);
            }
        },
        prefillAddress(property) {
            // Format town as it seems to come as all capitals
            let town = property.post_town;
            town = town.toLowerCase();
            town = town.charAt(0).toUpperCase() + town.substr(1);

            // Set our address field as the results of what was selected
            this.details.address = property.line_1;
            this.details.address2 = property.line_2;
            this.details.postcode = property.postcode;
            this.details.town = town;
            this.details.county = property.county;
            this.details.country = property.country;
        },
        prepopulateForm() {
            if (this.authenticated) {
                this.details.first_name = this.user.first_name;
                this.details.last_name = this.user.last_name;
                this.details.email = this.user.email;
                this.details.telephone = this.user.telephone;
                this.details.mobile_telephone = this.user.mobile_telephone;
            }
        },
        showProductInfo(product) {
            this.modalProduct = product;
            this.$nextTick(() => {
                this.$refs.modalProductDescription.show();
            });
        },
        getWindowSize() {
            this.windowSize = window.innerHeight;
        },
        handleScroll() {
            this.scrolled = window.scrollY;
            this.showSticky = this.scrolled > this.windowSize;
        },
        setStep(step) {
            this.step = step;
            const ref = this.step && this.$refs[`accordion${this.step.charAt(0).toUpperCase() + step.slice(1)}`];

            if (ref) {
                ref.show = true;
            }
        }
    }
};
</script>

<style lang="scss">
button {
    box-shadow: none;
    background: transparent;
    border: 0;
    cursor: pointer;

    &:focus, &:hover, &:active {
        outline: none !important;
    }
}
// Custom card styling for basket accordion
#basket-accordion {
    .card {
        background: transparent;
        border: 0;
        border-radius: 0;
        margin-bottom: 0;
    }

    .card-header {
        padding: 0;
        border: 0;
        background: white;

        > a,
        > button {
            display: block;
            width: 100%;
            text-align: left;
            padding: 2rem;
            transition: opacity .5s;
            color: theme-color('dark-grey');

            &:hover {
                text-decoration: none;
                color: theme-color('dark-grey');
            }
        }
    }

    .card-body {
        padding: 3rem;
        margin-bottom: -2px;
        border-left: 4px solid white;
        border-right: 4px solid white;
        border-bottom: 4px solid white;
    }

    #accordion2 {
        .card {
            background: white;
            border-radius: 0;
        }
    }
}

// disabled class
.disabled {
    pointer-events: none;
    opacity: .3;
}

// feature
.feature-icon {
    width: 2rem;
    margin-right: 2rem;
    color: theme-color('primary');
}

// extras
.extras-icon {
    width: 4rem;
    color: theme-color('primary');
    margin-bottom: 2rem;
}

// basket items
.basket-items {
    .table-flex--header {
        display: flex !important;
    }
}

.basket-item {
    flex-flow: row !important;
    @include font-size(1.2rem);

    &--packages {
        > div:first-of-type {
            font-weight: 600 !important;
        }
    }

    > div {
        font-weight: 300 !important;
        color: theme-color('dark-grey');

        @include media-breakpoint-down(sm) {
            &:nth-of-type(1) {
                flex: 0 0 12rem;
            }

            &:nth-of-type(3) {
                flex: 0 0 8rem;
            }
        }
    }

    .remove {
        display: inline-block;
        position: relative;
        width: 10px;
        height: 10px;
        margin-left: 10px;
        cursor: pointer;

        &::before, &::after {
            content: "";
            width: 10px;
            height: 1px;
            position: absolute;
            top: 50%;
            left: 0;
            background: theme-color('mid-grey');
        }

        &::before {
            transform: rotate(45deg);
        }

        &::after {
            transform: rotate(-45deg);
        }
    }
}

// Price label
.price {
    &::before {
        content: "£";
    }
}

// Totals
.basket-totals {
    border-top: 1px solid theme-color('light-grey');
    margin-top: 3rem;
    padding-top: 3rem;
}

// package icon
.package-icon-basket {
    width: 6rem;
    margin-right: 2rem;
}

// steps
.basket-steps {
    display: flex;
    align-items: center;

    &::before {
        content: 'Steps';
        margin-right: 1rem;
        font-weight: bold;
    }

    &__item {
        margin: 0 1rem;
        border-radius: 50%;
        width: 30px;
        height: 30px;
        background: darken(theme-color('light-grey'), 10%);
        color: white;
        display: flex;
        align-items: center;
        justify-content: center;
        position: relative;
        font-weight: bold;

        &.active {
            background: theme-color('primary');
        }

        &::after {
            content: "";
            width: 100%;
            height: 2px;
            background: darken(theme-color('light-grey'), 10%);
            position: absolute;
            top: 50%;
            left: 100%;
            transform: translateY(-50%);
        }

        &:last-of-type {
            &::after {
                display: none;
            }
        }
    }
}

.product-included {
    opacity: 0.5;
}

.package-my-basic,
.package-intro-only {
    .package__header > a {
        color: theme-color('sell') !important;
    }
}

.package-my-classic,
.package-tenant-find {
    .package__header > a {
        color: theme-color('let') !important;
    }
}

.package-my-premium,
.package-tenant-find-plus {
    .package__header > a {
        color: theme-color('rent') !important;
    }
}

.package-item {
    @include font-size(1.8rem);

    @include media-breakpoint-up(lg) {
        @include font-size(2.5rem);
    }
}

.expiry-cv2 {
    flex-wrap: wrap;

    @include media-breakpoint-up(xl) {
        flex-wrap: nowrap;
    }
}

.expiry-field,
.cv2-field {
    flex: 1;
}

.expiry-fields {
    flex: 0 0 100%;

    @include media-breakpoint-up(xl) {
        flex: 0 0 66%;
    }
}

.expiry-field {
    flex: 0 0 48%;
    margin-right: .5rem;

    &:last-of-type {
        margin-right: 0;
    }

    @include media-breakpoint-up(xl) {
        flex: 0 0 48%;
    }
}

.cv2-field {
    flex: 1;

    @include media-breakpoint-up(xl) {
        flex: 0 0 30%;
    }
}

.login-form-checkout {
    margin: 0 auto;
}

.login-modal {
    .modal-content {
        background: theme-color('light-grey');
    }
}

.info-icon {
    width: 2rem;
}

.current-package {
    font-family: $font-family-sans-serif;
    color: theme-color('secondary');
    @include font-size(1.4rem);
    display: none;

    @include media-breakpoint-up(md) {
        display: inline-block;
    }
}

.total-sticky {
    display: block;
    position: fixed;
    bottom: 0;
    left: 0;
    margin: 1rem;
    background: theme-color('primary');
    color: white;
    padding: 1rem;
    min-width: 100px;
    text-align: center;
    z-index: 10;
    border-radius: global-var('rounded-corners');

    @include media-breakpoint-up(md) {
        display: none;
    }
}
</style>
